import IFilterValue from '../models/iFilterValue';
import { EnabledProductTypes } from '../types/ProductTypeFilter';

// Normalized filter for consistent caching (removes changing IDs)
export interface NormalizedFilter {
  attributeName: string;
  selectedValue?: string;
  lowerBound?: number;
  upperBound?: number;
}

export interface SearchParams {
  bearerToken: string;
  filters: IFilterValue[];
  indexNames: string[];
  options: {
    salesOrgDivs?: number[];
    isExact?: boolean;
    enabledProductTypes?: EnabledProductTypes;
    correlationId?: string | null;
  };
}

export interface FacetParams {
  bearerToken: string;
  currentFilters: IFilterValue[];
  attributeName: string;
  indexNames: string[];
  salesOrgDivs?: number[];
  enabledProductTypes: EnabledProductTypes;
}

// Helper function to normalize filters for consistent cache keys
const normalizeFilters = (filters: IFilterValue[]): NormalizedFilter[] => {
  return filters
    .filter((filter) => filter.attribute) // Only include filters with attributes
    .map((filter) => ({
      attributeName: filter.attribute!.attributeName,
      selectedValue: filter.selectedValue,
      lowerBound: filter.lowerBound,
      upperBound: filter.upperBound,
    }))
    .sort((a, b) => a.attributeName.localeCompare(b.attributeName)); // Sort for consistency
};

// Helper function to create normalized search params for caching
const normalizeSearchParams = (searchParams: SearchParams) => {
  return {
    // Exclude bearerToken from cache key (authentication handled at request level)
    filters: normalizeFilters(searchParams.filters),
    indexNames: [...searchParams.indexNames].sort((a, b) => a.localeCompare(b)), // Sort for consistency
    options: {
      salesOrgDivs: searchParams.options.salesOrgDivs?.slice().sort((a, b) => a - b), // Sort for consistency
      isExact: searchParams.options.isExact,
      enabledProductTypes: searchParams.options.enabledProductTypes,
      // Exclude correlationId from cache key (it's for tracking, not results)
    },
  };
};

// Helper function to normalize facet params for caching
const normalizeFacetParams = (facetParams: FacetParams) => {
  return {
    // Exclude bearerToken from cache key (authentication handled at request level)
    currentFilters: normalizeFilters(facetParams.currentFilters),
    attributeName: facetParams.attributeName,
    indexNames: [...facetParams.indexNames].sort((a, b) => a.localeCompare(b)), // Sort for consistency
    salesOrgDivs: facetParams.salesOrgDivs?.slice().sort((a, b) => a - b), // Sort for consistency
    enabledProductTypes: facetParams.enabledProductTypes,
  };
};

export const queryKeys = {
  products: (searchParams: SearchParams) => ['products', normalizeSearchParams(searchParams)] as const,
  attributes: (indexNames: string[]) => ['attributes', [...indexNames].sort((a, b) => a.localeCompare(b))] as const,
  facets: (facetParams: FacetParams) => ['facets', normalizeFacetParams(facetParams)] as const,
  indexes: () => ['indexes'] as const,
  salesOrgs: () => ['salesOrgs'] as const,
  featureFlags: () => ['featureFlags'] as const,
} as const;
